import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import "./caseFilterForm.scss";
import { CaseFilterParams } from "../../../models/CaseFilterParams/caseFilterParams.model";
import { Form, Formik, FormikValues } from "formik";
import { Button, Card, Col, DatePicker, Modal, Row, Switch, Tooltip } from "antd";
import {
  FilterOutlined,
  SyncOutlined,
  ExportOutlined,
  TableOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import moment from "moment";
import DropdownField from "../DropdownField";
import ScanCenterService from "../../../services/ScanCenter/scanCenter.service";
import { ScanCenter } from "../../../models/ScanCenter/scanCenter.model";
import {
  getDropdownOptions,
  IDropdownOptions,
} from "../../utils/dropdownUtils";
import DoctorService from "../../../services/Doctor/doctor.service";
import { Doctor } from "../../../models/Doctor/doctor.model";
import ScanTypeService from "../../../services/ScanType/scanType.service";
import { ScanType } from "../../../models/ScanType/scanType.model";
import BodyPartService from "../../../services/BodyPart/bodyPart.service";
import { BodyPart } from "../../../models/BodyPart/bodyPart.model";
import { CaseStatusEnum } from "../../../enums/caseStatus.enum";
import { ScanCenterBranch } from "../../../models/ScanCenterBranch/scanCenterBranch.model";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import InputField from "../InputField";
import { useHistory, useLocation } from "react-router-dom";
import { AdminTypeEnum } from "../../../enums/AdminType.enum";
import TagService from "../../../services/TagService/tag.service";
import { Tag } from "../../../models/Tag/tag.model";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import AdminService from "../../../services/Admin/admin.service";
import AppSync from "../AppSync";
import { AdminFeatureEnum } from "../../../enums/AdminFeature.enum";
import {
  ADMIN_CASES_LOCKED_COLUMN_ORDER,
  LISTING_CONTEXT,
} from "../../../constants/listingColumnKeys";
import TableLayoutPanel from "../TableLayoutPanel";

interface CaseFilterFormProps extends AuthReducerProps {
  formValues: CaseFilterParams;
  onSubmit?: (params: CaseFilterParams) => void;
  onChange?: (params: CaseFilterParams) => void;
  /** Clears filter query string and restores defaults (parent should replace URL + refetch). */
  onReset?: () => void;
  isAdmin?: boolean;
  isDoctor?: boolean;
  isScanCenter?: boolean;
  onSync: (params: CaseFilterParams, isSyncing?: boolean) => void;
  loading?: boolean;
  /** Refetch listing column preferences after the user saves from the Columns dialog. */
  onListingColumnsSaved?: () => void;
}

const useFilterPersistence = (initialValues: CaseFilterParams) => {
  const history = useHistory();
  const location = useLocation();

  // Parse URL parameters on initial load
  const getInitialFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    const urlFilters: Partial<CaseFilterParams> = {};

    // Map of parameter names to their parsing logic
    const paramParsers: Record<string, (value: string) => any> = {
      patientId: (v) => v,
      patientName: (v) => v,
      referringDoctor: (v) => v,
      scanCenterId: (v) => (v ? Number(v) : undefined),
      doctorId: (v) => (v ? Number(v) : undefined),
      teleradAdminId: (v) => (v ? Number(v) : undefined),
      tagId: (v) => (v ? Number(v) : undefined),
      scanTypeId: (v) => (v ? Number(v) : undefined),
      bodyPartId: (v) => (v ? Number(v) : undefined),
      status: (v) => v,
      fromDate: (v) => v,
      toDate: (v) => v,
    };

    // Parse each parameter
    Object.keys(paramParsers).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) {
        urlFilters[key as keyof CaseFilterParams] = paramParsers[key](value);
      }
    });

    // Merge URL parameters with initial values
    return { ...initialValues, ...urlFilters };
  };

  // Update URL with current filter values
  const updateUrlParams = useCallback(
    (values: CaseFilterParams) => {
      const searchParams = new URLSearchParams();

      // Add non-empty filter values to URL
      (Object.keys(values) as Array<keyof CaseFilterParams>).forEach((key) => {
        const value = values[key];
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      // Update URL without page reload
      history.replace(`${location.pathname}?${searchParams.toString()}`);
    },
    [location.pathname, history]
  );

  return { getInitialFilters, updateUrlParams };
};

function CaseFilterForm({
  loading,
  isAdmin,
  isDoctor,
  isScanCenter,
  formValues,
  user,
  userRole,
  onReset,
  onSubmit,
  onChange,
  onSync,
  adminType,
  onListingColumnsSaved,
}: CaseFilterFormProps) {

  const adminFeatures = useMemo(() => {
    return user?.adminFeatures?.map((adminFeature) => adminFeature.feature);
  }, [user]);

  const { getInitialFilters, updateUrlParams } = useFilterPersistence(
    formValues
  );

  const [showFilters, setShowFilters] = useState(window.innerWidth > 768);

  const [scanTypeOptions, setScanTypeOptions] = useState<IDropdownOptions[]>(
    []
  );

  const [bodyPartOptions, setBodyPartOptions] = useState<IDropdownOptions[]>(
    []
  );

  const [selectedScanCenterId, setSelectedScanCenterId] = useState<number>();

  const [doctorOptions, setDoctorOptions] = useState<IDropdownOptions[]>([]);

  const [tagOptions, setTagOptions] = useState<IDropdownOptions[]>([]);

  const [scanCenterOptions, setScanCenterOptions] = useState<
    IDropdownOptions[]
  >([]);

  const [patientNameSearch, setPatientNameSearch] = useState("");

  const [patientIdSearch, setPatientIdSearch] = useState("");

  const [exportLoading, setExportLoading] = useState(false);

  const [columnsModalOpen, setColumnsModalOpen] = useState(false);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const listingContext = useMemo(() => {
    if (userRole === UserRoleEnum.ADMIN) return LISTING_CONTEXT.ADMIN_CASES;
    if (userRole === UserRoleEnum.DOCTOR) return LISTING_CONTEXT.DOCTOR_CASES;
    if (userRole === UserRoleEnum.SCAN_CENTER) return LISTING_CONTEXT.SCAN_CENTER_CASES;
    return null;
  }, [userRole]);

  const toggleFilterForm = () => {
    setShowFilters(!showFilters);
  };

  const handleResetFilters = () => {
    setSelectedScanCenterId(undefined);
    setPatientNameSearch("");
    setPatientIdSearch("");
    onReset?.();
  };

  const doctorStatusOptions = [
    { label: "Assigned", value: CaseStatusEnum.ASSIGNED },
    { label: "Unassigned", value: CaseStatusEnum.UNASSIGNED },
    { label: "Reported", value: CaseStatusEnum.REPORTED },
  ];

  const adminStatusOptions = [
    { label: "Assigned", value: CaseStatusEnum.ASSIGNED },
    { label: "Unassigned", value: CaseStatusEnum.UNASSIGNED },
    { label: "Reported", value: CaseStatusEnum.REPORTED },
    { label: "To be approved", value: CaseStatusEnum.TO_BE_APPROVED },
    { label: "Report Pending", value: CaseStatusEnum.REPORT_PENDING },
  ];

  const otherStatusOptions = [
    { label: "Reported", value: CaseStatusEnum.REPORTED },
    { label: "Report Pending", value: CaseStatusEnum.REPORT_PENDING },
  ];

  const handleExportExcel = (formValues: FormikValues) => {
    setExportLoading(true);
    if(isScanCenter){
      ScanTypeService.exportScanCenterExcel(
          formValues,
          (targetUrl: any) => {
            window.open(targetUrl, "_blank");
          },
          () => {},
          () => {
            setExportLoading(false);
          }
      );
    }
    if(isAdmin){
      AdminService.exportAdminExcel(
          formValues,
          (targetUrl: any) => {
            window.open(targetUrl, "_blank");
          },
          () => {},
          () => {
            setExportLoading(false);
          }
      );
    }
    if(isDoctor){
      DoctorService.exportDoctorExcel(
          formValues,
          (targetUrl: any) => {
            window.open(targetUrl, "_blank");
          },
          () => {},
          () => {
            setExportLoading(false);
          }
      );
    }

  };

  useEffect(() => {
    if(isAdmin || isDoctor) {
       ScanCenterService.fetchScanCenters(
        (scanCenters: ScanCenter[]) => {
          const options = scanCenters.map((scanCenter) => ({
            label: scanCenter.name ?? "",
            value: scanCenter?.type === AdminTypeEnum.SCAN_CENTER_ADMIN ? `admin-${scanCenter?.id}` : `scanCenter-${scanCenter?.id}`,
            type: scanCenter.type,
            id: scanCenter?.id,
          }));
          setScanCenterOptions(options);
        },
        () => {},
        () => {},
        userRole,
        true
      );
    }
    if (isAdmin) {
      DoctorService.fetchAssignmentDoctors(
        userRole as UserRoleEnum,
        (doctors: Doctor[]) => {
          setDoctorOptions(
            doctors.map((doctor) => ({
              label: doctor.assignmentGroupLabel
                ? `${doctor.assignmentGroupLabel} — ${doctor.doctorName}`
                : doctor.doctorName,
              value: doctor?.type === UserRoleEnum.ADMIN ? `admin-${doctor?.id}` : `doctor-${doctor?.id}`,
              roleType: doctor.type,
              id: doctor?.id,
            }))
          );
        },
        () => {},
        () => {}
      );
    }
    ScanTypeService.fetchScanTypes(
      (scanTypes: ScanType[]) => {
        setScanTypeOptions(getDropdownOptions(scanTypes, "name", "id"));
      },
      () => {},
      () => {}
    );
    const scanCenterId = isScanCenter
      ? Number(user?.id)
      : Number(selectedScanCenterId);

    TagService.fetchAdminTags(
      userRole as UserRoleEnum,
      (tags: Tag[]) => {
        setTagOptions(getDropdownOptions(tags, "formattedName", "id"));
      },
      () => {},
      () => {}
    );

    BodyPartService.fetchBodyParts(
      (bodyParts: BodyPart[]) => {
        setBodyPartOptions(getDropdownOptions(bodyParts, "name", "id"));
      },
      () => {},
      () => {}
    );
  }, [userRole]);

  return (
    <div className="case-filter-form">
      <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
        <Button
          className="case-filter-form__toggle-button"
          type="primary"
          onClick={toggleFilterForm}
          icon={<FilterOutlined />}
        />
      </Tooltip>
      {onReset ? (
        <Tooltip title="Reset all filters">
          <Button
            type="primary"
            className="case-filter-form__reset-button"
            icon={<UndoOutlined />}
            onClick={handleResetFilters}
          />
        </Tooltip>
      ) : null}
      <Tooltip title="Sync Cases">
        <Button
            onClick={() => {
              onSync(formValues, true);
            }}
            className="case-filter-form__sync-button"
            type="primary"
            icon={<SyncOutlined />}
        />
      </Tooltip>
      {listingContext && (
        <Tooltip title="Rearrange Columns">
          <Button
            type="primary"
            className="case-filter-form__columns-button"
            icon={<TableOutlined />}
            onClick={() => setColumnsModalOpen(true)}
          />
        </Tooltip>
      )}
      <Tooltip title="Export to Excel">
        <Button
            onClick={() => {
              handleExportExcel(formValues)
            }}
            className="case-filter-form__export-button"
            type="primary"
            icon={<ExportOutlined />}
            loading={exportLoading}
        />
      </Tooltip>
      {loading ?<div className="case-filter-form__sync-container"><AppSync /> </div>: null}
      <Modal
        title="Customize columns"
        visible={columnsModalOpen}
        onCancel={() => setColumnsModalOpen(false)}
        footer={null}
        width={440}
        destroyOnClose
      >
        <Card bordered={false} bodyStyle={{ padding: "8px 0 0" }}>
          <TableLayoutPanel
            userRole={userRole}
            listingContext={listingContext}
            nonReorderableKeys={
              listingContext === LISTING_CONTEXT.ADMIN_CASES
                ? ADMIN_CASES_LOCKED_COLUMN_ORDER
                : undefined
            }
            onSaved={() => {
              onListingColumnsSaved?.();
              setColumnsModalOpen(false);
            }}
          />
        </Card>
      </Modal>
      <Formik
        initialValues={getInitialFilters()} // Use URL-aware initial values
        onSubmit={(values) => {
          updateUrlParams(values);
          onSubmit?.(values);
        }}
        enableReinitialize
        validationSchema={null}
      >
        {({ values, setFieldValue }) => {
          const handleFieldChange = (fieldName: string, value: any) => {
            setFieldValue(fieldName, value); // Update the field value in Formik state

            // Update URL params on each field change
            updateUrlParams({ ...values, [fieldName]: value, page: 1 });

            if (fieldName === "patientName") {
              setPatientNameSearch(value);
            }

            if (fieldName === "patientId") {
              setPatientIdSearch(value);
            }

            // Trigger the onChange callback with the updated form values
            if (onChange) {
              onChange({ ...values, [fieldName]: value });
            }
          };

          const handleMultipleFieldChange = (updatedFields: Partial<CaseFilterParams>) => {
            Object.entries(updatedFields).forEach(([key, value]) => {
              setFieldValue(key, value);
            });

            const newValues = {
              ...values,
              ...updatedFields,
              page: 1,
            };

            updateUrlParams(newValues);

            if (onChange) {
              onChange(newValues);
            }
          };

          const selectedDoctorValue =
            doctorOptions?.length === 0
              ? undefined
              : values.doctorId
                ? `doctor-${values.doctorId}`
                : values.teleradAdminId
                  ? `admin-${values.teleradAdminId}`
                  : undefined;

          const selectedScanCenterValue = 
            scanCenterOptions?.length === 0
              ? undefined
              : values.scanCenterId
                ? `scanCenter-${values.scanCenterId}`
                : values.scanCenterAdminId
                  ? `admin-${values.scanCenterAdminId}`
                  : undefined;

          return (
            <Form>
              <Row gutter={[20, 10]} align="middle">
                {showFilters && (
                  <>
                    <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                      <InputField
                        allowClear
                        placeholder="Patient ID"
                        title="Patient ID"
                        name="patientId"
                        type={"string"}
                        value={values.patientId}
                        onChange={(event: any) => {
                          handleFieldChange("patientId", event.target.value);
                        }}
                      />
                    </Col>
                    <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                      <InputField
                        allowClear
                        placeholder="Patient Name"
                        title="Patient Name"
                        name="patientName"
                        type={"string"}
                        value={values.patientName}
                        onChange={(event: any) => {
                          handleFieldChange("patientName", event.target.value);
                        }}
                      />
                    </Col>
                    <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                      <DropdownField
                        allowClear
                        placeHolder="Scan Type"
                        showSearch
                        title="Scan Type"
                        name="scanTypeId"
                        setFieldValue={setFieldValue}
                        options={scanTypeOptions}
                        value={values.scanTypeId}
                        onChange={(value) => {
                          handleFieldChange("scanTypeId", value);
                        }}
                      />
                    </Col>
                    <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                      <DropdownField
                        allowClear
                        placeHolder="Body Part"
                        showSearch
                        title="Body Part"
                        name="bodyPartId"
                        setFieldValue={setFieldValue}
                        options={bodyPartOptions}
                        value={values.bodyPartId}
                        onChange={(value) => {
                          handleFieldChange("bodyPartId", value);
                        }}
                      />
                    </Col>
                    {(user?.adminType === AdminTypeEnum.SCAN_CENTER_ADMIN ||
                      user?.adminType ===
                        AdminTypeEnum.DIGITAL_SHARING_ADMIN) && (
                      <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                        <InputField
                          allowClear
                          placeholder="Referring Doctor"
                          title="Referring Doctor"
                          name="referringDoctor"
                          type={"string"}
                          value={values?.referringDoctor}
                          onChange={(event: any) => {
                            const { value } = event.target;
                            handleFieldChange("referringDoctor", value);
                          }}
                        />
                      </Col>
                    )}
                    {
                      (isAdmin || isDoctor) &&
                      <>
                        <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                          <DropdownField
                            allowClear
                            placeHolder="Scan Center"
                            showSearch
                            title="Scan Center"
                            name="scanCenterId"
                            setFieldValue={setFieldValue}
                            options={scanCenterOptions}
                            value={selectedScanCenterValue}
                            onChange={(value, record) => {
                              if(!value) {
                                setSelectedScanCenterId(undefined);
                                handleMultipleFieldChange({ 
                                  scanCenterId: undefined, 
                                  scanCenterAdminId: undefined, 
                                  scanCenterBranchId: undefined 
                                });
                                return;
                              }
                              setSelectedScanCenterId(record?.id);
                              handleMultipleFieldChange(
                                  record.type === AdminTypeEnum.SCAN_CENTER_ADMIN
                                    ? { scanCenterAdminId: record?.id, scanCenterId: undefined, scanCenterBranchId: undefined }
                                    : { scanCenterId: record?.id, scanCenterAdminId: undefined, scanCenterBranchId: undefined }
                                );
                            }}
                          />
                        </Col>
                      </>
                    }
                    {isAdmin && (
                      <React.Fragment>
                        {user?.adminType !==
                          AdminTypeEnum.DIGITAL_SHARING_ADMIN && (
                          <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                            <DropdownField
                              allowClear
                              placeHolder="Doctor"
                              showSearch
                              title="Doctor"
                              name="doctorId"
                              setFieldValue={setFieldValue}
                              options={doctorOptions}
                              value={selectedDoctorValue}
                              onChange={(value, option) => {
                                if(!value) {
                                  handleMultipleFieldChange({ 
                                    doctorId: undefined, 
                                    teleradAdminId: undefined 
                                  });
                                  return;
                                }
                                handleMultipleFieldChange(
                                  option.roleType === "admin"
                                    ? { doctorId: undefined, teleradAdminId: option?.id }
                                    : { doctorId: option?.id, teleradAdminId: undefined }
                                );
                              }}
                            />
                          </Col>
                        )}
                      </React.Fragment>
                    )}
                    {user?.adminType !==
                      AdminTypeEnum.DIGITAL_SHARING_ADMIN && (
                      <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                        <DropdownField
                          allowClear
                          placeHolder="Select Status"
                          title="Status"
                          name="status"
                          setFieldValue={setFieldValue}
                          options={
                            isAdmin
                              ? adminStatusOptions
                              : isDoctor
                              ? doctorStatusOptions
                              : otherStatusOptions
                          }
                          value={values.status}
                          onChange={(value) => {
                            handleFieldChange("status", value);
                          }}
                        />
                      </Col>
                    )}
                    {user?.adminType !==
                      AdminTypeEnum.DIGITAL_SHARING_ADMIN && (
                      <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                        <DropdownField
                          allowClear
                          placeHolder="Select Tag"
                          showSearch
                          title="Tag"
                          name="tagId"
                          setFieldValue={setFieldValue}
                          options={tagOptions}
                          value={values.tagId}
                          onChange={(value) => {
                            handleFieldChange("tagId", value);
                          }}
                        />
                      </Col>
                    )}
                    <Col span={4} xs={12} md={4} lg={4} xl={4} xxl={4}>
                      <InputField
                        allowClear
                        placeholder="Interesting Keyword"
                        title="Keyword"
                        name="interestingKeyword"
                        type={"string"}
                        value={values?.interestingKeyword}
                        onChange={(event: any) => {
                          const { value } = event.target;
                          handleFieldChange("interestingKeyword", value);
                        }}
                      />
                    </Col>
                    {
                      adminFeatures && adminFeatures?.includes(AdminFeatureEnum.ACQUISITION_DATE) && (
                        <Col span={5} sm={20} xs={20} md={5} lg={5} xl={5} xxl={5}>
                          <div className="date-picker-field">
                            <div className="date-picker-field__title">
                              Study Date Range
                            </div>
                            <DatePicker.RangePicker
                              className="date-picker-upload-date-range"
                              allowClear={true}
                              value={
                                values.acquisitionFromDate && values.acquisitionToDate
                                  ? [
                                      moment(values.acquisitionFromDate, "YYYY-MM-DD"),
                                      moment(values.acquisitionToDate, "YYYY-MM-DD"),
                                    ]
                                  : undefined
                              }
                              format="DD/MM/YYYY"
                              ranges={{
                                Today: [moment().startOf("day"), moment().endOf("day")],
                                Yesterday: [
                                  moment().subtract(1, "days").startOf("day"),
                                  moment().subtract(1, "days").endOf("day"),
                                ],
                                "Last 2 days": [
                                  moment().subtract(2, "days").startOf("day"),
                                  moment().endOf("day"),
                                ],
                                "Last 7 days": [
                                  moment().subtract(7, "days").startOf("day"),
                                  moment().endOf("day"),
                                ],
                                "Last 30 days": [
                                  moment().subtract(30, "days").startOf("day"),
                                  moment().endOf("day"),
                                ],
                                "This month": [
                                  moment().startOf("month"),
                                  moment().endOf("month"),
                                ],
                                "Last month": [
                                  moment().subtract(1, "months").startOf("month"),
                                  moment().subtract(1, "months").endOf("month"),
                                ],
                              }}
                              onChange={(dates) => {
                                if (dates && dates[0] && dates[1]) {
                                  handleMultipleFieldChange({
                                    acquisitionFromDate: dates[0].format("YYYY-MM-DD"),
                                    acquisitionToDate: dates[1].format("YYYY-MM-DD"),
                                  });
                                } else {
                                  handleMultipleFieldChange({
                                    acquisitionFromDate: undefined,
                                    acquisitionToDate: undefined,
                                  });
                                }
                              }}
                            />
                          </div>
                        </Col>
                      )
                    }
                    <Col span={5} sm={20} xs={20} md={5} lg={5} xl={5} xxl={5}>
                      <div className="date-picker-field">
                        <div className="date-picker-field__title">
                          Uploaded Date Range
                        </div>
                        <DatePicker.RangePicker
                          className="date-picker-upload-date-range"
                          allowClear={false}
                          value={
                            values.fromDate && values.toDate
                              ? [
                                  moment(values.fromDate, "YYYY-MM-DD"),
                                  moment(values.toDate, "YYYY-MM-DD"),
                                ]
                              : undefined
                          }
                          format="DD/MM/YYYY"
                          ranges={{
                            Today: [moment().startOf("day"), moment().endOf("day")],
                            Yesterday: [
                              moment().subtract(1, "days").startOf("day"),
                              moment().subtract(1, "days").endOf("day"),
                            ],
                            "Last 2 days": [
                              moment().subtract(2, "days").startOf("day"),
                              moment().endOf("day"),
                            ],
                            "Last 7 days": [
                              moment().subtract(7, "days").startOf("day"),
                              moment().endOf("day"),
                            ],
                            "Last 30 days": [
                              moment().subtract(30, "days").startOf("day"),
                              moment().endOf("day"),
                            ],
                            "This month": [
                              moment().startOf("month"),
                              moment().endOf("month"),
                            ],
                            "Last month": [
                              moment().subtract(1, "months").startOf("month"),
                              moment().subtract(1, "months").endOf("month"),
                            ],
                          }}
                          onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                              handleMultipleFieldChange({
                                fromDate: dates[0].format("YYYY-MM-DD"),
                                toDate: dates[1].format("YYYY-MM-DD"),
                              });
                            }
                          }}
                        />
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default AuthContainer(CaseFilterForm);
